/**
 * Editable source panel for a custom_code_node's CURRENT pin.
 *
 * The immutable run view (`SourceCodePanel`) answers "what did this run
 * execute"; this editor answers "what will the node run next" and lets
 * the user change it. Saving is always the explicit two-step the
 * platform requires: validate + commit (a NEW git commit on the
 * workflow's software-repo branch) then repin (a NEW workflow version
 * with the new script_sha). Nothing is ever mutated in place, and the
 * confirmation dialog shows the old -> new SHA before anything lands.
 *
 * Capabilities: multi-file tabs with dirty markers, CodeMirror editing
 * (Python/JSON highlighting), text-file upload into the change set, a
 * new-script scaffold for unpinned nodes, a read-only fallback when the
 * caller lacks edit permission or the workflow is locked, and typed
 * error display (manifest violations, guardrail rejections, stale pins,
 * concurrent edits) from the backend's `{code, message}` contract.
 */

import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import CodeMirror from '@uiw/react-codemirror';
import {
  AlertTriangle,
  Check,
  FilePlus2,
  GitCommitVertical,
  Loader2,
  Lock,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  commitNodeCode,
  getNodeCode,
  NodeCodeError,
  repinNodeCode,
  type NodeCodeResponse,
} from '@/services/workflow/node-code';
import { useNodeStore } from '@/stores/node-store';

import { SourceCodePanel } from './source-code-panel';

const shortSha = (sha: string) => (sha ? sha.slice(0, 12) : '(unpinned)');

/** Upload cap mirrors the backend per-file byte guardrail (1 MB). */
const MAX_UPLOAD_BYTES = 1_000_000;

const UPLOAD_ACCEPT =
  '.py,.pyi,.json,.txt,.md,.yaml,.yml,.toml,.cfg,.ini,.sql,.csv';

/** `File.text()` with a FileReader fallback (jsdom lacks `.text()`). */
const readFileText = (file: File): Promise<string> =>
  typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });

export function scaffoldFiles(name: string): Record<string, string> {
  const safe = (name || 'my-node')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return {
    'node.json': `${JSON.stringify(
      {
        name: safe || 'my-node',
        description: 'Describe what this node computes.',
        entrypoint: 'main.py',
        runtime: 'python',
        input_schema: { type: 'object' },
        output_schema: { type: 'object' },
        network: { allowed_hosts: [] },
        timeout_seconds: 120,
      },
      null,
      2,
    )}\n`,
    'main.py': [
      '"""Lambda node entrypoint.',
      '',
      'Reads JSON input from the file at env NODE_INPUT and writes the',
      'JSON result to the file at env NODE_OUTPUT. Deterministic: same',
      'input at the same pinned commit -> same output.',
      '"""',
      '',
      'import json',
      'import os',
      '',
      '',
      'def main() -> None:',
      '    with open(os.environ["NODE_INPUT"], encoding="utf-8") as f:',
      '        payload = json.load(f)',
      '',
      '    result = {"echo": payload}',
      '',
      '    with open(os.environ["NODE_OUTPUT"], "w", encoding="utf-8") as f:',
      '        json.dump(result, f)',
      '',
      '',
      'if __name__ == "__main__":',
      '    main()',
      '',
    ].join('\n'),
  };
}

/** Slug used for the default new-script directory. */
export function defaultScriptPath(nodeDisplayName: string): string {
  const slug = (nodeDisplayName || 'custom-code')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `nodes/${slug || 'custom-code'}`;
}

type SaveStep = 'idle' | 'confirm' | 'committing' | 'repinning' | 'done';

interface EditorFile {
  relative_path: string;
  content: string;
  language: string;
  isNew: boolean;
}

interface SourceCodeEditorProps {
  orgId: string;
  workflowId: string;
  nodeId: string;
}

export function SourceCodeEditor({
  orgId,
  workflowId,
  nodeId,
}: SourceCodeEditorProps) {
  const [source, setSource] = useState<NodeCodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<NodeCodeError | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);

  // Draft state: edited contents for existing files plus wholly new files.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newFiles, setNewFiles] = useState<Record<string, string>>({});
  const [selectedPath, setSelectedPath] = useState('');

  // New-script scaffold state (unpinned nodes only).
  const [scaffoldPath, setScaffoldPath] = useState('');
  const [scaffolded, setScaffolded] = useState(false);

  const [saveStep, setSaveStep] = useState<SaveStep>('idle');
  const [saveError, setSaveError] = useState<NodeCodeError | null>(null);
  const [lastSave, setLastSave] = useState<{
    oldSha: string;
    newSha: string;
    versionNumber: number;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleParameterChange = useNodeStore(
    (state) => state.handleParameterChange,
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    getNodeCode(orgId, workflowId, nodeId, controller.signal)
      .then((result) => {
        setSource(result);
        setDrafts({});
        setNewFiles({});
        setScaffolded(false);
        setScaffoldPath(defaultScriptPath(result.node_display_name));
        setSelectedPath(
          (current) =>
            (result.files.some((f) => f.relative_path === current) &&
              current) ||
            result.entrypoint ||
            result.files[0]?.relative_path ||
            '',
        );
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) {
          setLoadError(
            caught instanceof NodeCodeError
              ? caught
              : new NodeCodeError('Source could not be loaded.'),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [orgId, workflowId, nodeId, requestVersion]);

  const files: EditorFile[] = useMemo(() => {
    if (!source) return [];
    const existing = source.files.map((f) => ({
      relative_path: f.relative_path,
      content: drafts[f.relative_path] ?? f.content,
      language: f.language,
      isNew: false,
    }));
    const added = Object.entries(newFiles)
      .filter(([path]) => !source.files.some((f) => f.relative_path === path))
      .map(([path, content]) => ({
        relative_path: path,
        content,
        language: path.endsWith('.py')
          ? 'python'
          : path.endsWith('.json')
            ? 'json'
            : 'plaintext',
        isNew: true,
      }));
    return [...existing, ...added].sort((a, b) =>
      a.relative_path.localeCompare(b.relative_path),
    );
  }, [source, drafts, newFiles]);

  const changedPaths = useMemo(() => {
    const paths = new Set<string>(Object.keys(newFiles));
    if (source) {
      for (const [path, content] of Object.entries(drafts)) {
        const original = source.files.find((f) => f.relative_path === path);
        if (!original || original.content !== content) paths.add(path);
      }
    }
    return paths;
  }, [source, drafts, newFiles]);

  const dirty = changedPaths.size > 0;
  const selectedFile =
    files.find((f) => f.relative_path === selectedPath) ?? files[0] ?? null;
  const canEdit = Boolean(source?.can_edit);

  const setFileContent = useCallback(
    (path: string, content: string, isNew: boolean) => {
      if (isNew) {
        setNewFiles((current) => ({ ...current, [path]: content }));
      } else {
        setDrafts((current) => ({ ...current, [path]: content }));
      }
    },
    [],
  );

  const handleUpload = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      setSaveError(null);
      for (const file of Array.from(fileList)) {
        if (file.size > MAX_UPLOAD_BYTES) {
          setSaveError(
            new NodeCodeError(
              `'${file.name}' is larger than the ${MAX_UPLOAD_BYTES.toLocaleString()}-byte per-file limit.`,
              'file_oversized',
            ),
          );
          continue;
        }
        const text = await readFileText(file);
        if (text.includes('\u0000')) {
          setSaveError(
            new NodeCodeError(
              `'${file.name}' looks binary; only UTF-8 text files are supported.`,
              'binary_file',
            ),
          );
          continue;
        }
        const exists = source?.files.some((f) => f.relative_path === file.name);
        setFileContent(file.name, text, !exists);
        setSelectedPath(file.name);
      }
    },
    [source, setFileContent],
  );

  const applyScaffold = useCallback(() => {
    if (!source) return;
    const scaffold = scaffoldFiles(source.node_display_name);
    setNewFiles((current) => ({ ...scaffold, ...current }));
    setScaffolded(true);
    setSelectedPath('main.py');
  }, [source]);

  const discardChanges = useCallback(() => {
    setDrafts({});
    setNewFiles({});
    setScaffolded(false);
    setSaveError(null);
  }, []);

  const performSave = useCallback(async () => {
    if (!source) return;
    setSaveError(null);
    setSaveStep('committing');
    try {
      const changed: Record<string, string> = {};
      for (const path of changedPaths) {
        const file = files.find((f) => f.relative_path === path);
        if (file) changed[path] = file.content;
      }
      const isNewScript = !source.pinned;
      const targetPath = isNewScript ? scaffoldPath.trim() : source.script_path;
      const commit = await commitNodeCode(orgId, workflowId, nodeId, {
        files: changed,
        message: isNewScript
          ? `Create custom code script ${targetPath}`
          : `Update ${source.script_path} via code editor`,
        ...(isNewScript ? { script_path: targetPath } : {}),
        ...(source.pinned ? { expected_pinned_sha: source.pinned_sha } : {}),
      });
      setSaveStep('repinning');
      const repin = await repinNodeCode(orgId, workflowId, nodeId, {
        new_sha: commit.commit_sha,
        expected_old_sha: source.pinned_sha,
        ...(isNewScript ? { script_path: targetPath } : {}),
      });
      // Reflect the new pin on the canvas without a manual refresh.
      handleParameterChange(nodeId, 'script_sha', repin.new_sha);
      handleParameterChange(nodeId, 'script_path', repin.script_path);
      setLastSave({
        oldSha: repin.old_sha,
        newSha: repin.new_sha,
        versionNumber: repin.new_workflow_version_number,
      });
      setSaveStep('done');
      // Reload the tree from the new pin (also clears drafts).
      setRequestVersion((value) => value + 1);
    } catch (caught: unknown) {
      setSaveStep('idle');
      setSaveError(
        caught instanceof NodeCodeError
          ? caught
          : new NodeCodeError('Saving the code change failed.'),
      );
    }
  }, [
    source,
    changedPaths,
    files,
    scaffoldPath,
    orgId,
    workflowId,
    nodeId,
    handleParameterChange,
  ]);

  if (loading) {
    return (
      <div
        data-testid='code-editor-loading'
        className='flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground'
      >
        <Loader2 className='size-4 animate-spin' />
        Loading current pinned source…
      </div>
    );
  }

  if (loadError || !source) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center'>
        <AlertTriangle className='size-6 text-amber-500' />
        <div>
          <p className='text-sm font-medium'>Code unavailable</p>
          <p className='mt-1 max-w-md text-xs text-muted-foreground'>
            {loadError?.message ?? 'Source could not be loaded.'}
          </p>
          {loadError?.code && (
            <p className='mt-1 font-mono text-[11px] text-muted-foreground'>
              {loadError.code}
            </p>
          )}
        </div>
        <Button
          size='sm'
          variant='outline'
          onClick={() => setRequestVersion((value) => value + 1)}
        >
          <RefreshCw className='size-3.5' />
          Retry
        </Button>
      </div>
    );
  }

  // Unpinned node without a scaffold yet: offer creation.
  if (!source.pinned && !scaffolded && files.length === 0) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center'>
        <FilePlus2 className='size-6 text-muted-foreground' />
        <div>
          <p className='text-sm font-medium'>No script pinned yet</p>
          <p className='mx-auto mt-1 max-w-md text-xs text-muted-foreground'>
            Create a new script directory in{' '}
            <span className='font-mono'>{source.repo_url}</span> (branch{' '}
            <span className='font-mono'>{source.branch}</span>). Saving commits
            the files and pins this node to the new commit.
          </p>
        </div>
        {canEdit ? (
          <div className='flex w-full max-w-sm flex-col gap-2'>
            <Input
              data-testid='scaffold-path-input'
              value={scaffoldPath}
              onChange={(event) => setScaffoldPath(event.target.value)}
              placeholder='nodes/my-node'
              className='font-mono text-xs'
            />
            <Button
              size='sm'
              onClick={applyScaffold}
              data-testid='scaffold-create'
            >
              <FilePlus2 className='size-3.5' />
              Create script scaffold
            </Button>
          </div>
        ) : (
          <p className='text-xs text-muted-foreground'>
            You need workflow edit permission to create a script.
          </p>
        )}
      </div>
    );
  }

  const saveDisabled =
    !dirty ||
    !canEdit ||
    saveStep === 'committing' ||
    saveStep === 'repinning' ||
    (!source.pinned && !scaffoldPath.trim());

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      <div className='shrink-0 space-y-2 border-b p-4'>
        <div className='flex flex-wrap items-start justify-between gap-2'>
          <div>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold'>
                Pinned code (editable)
              </span>
              {!canEdit && (
                <span className='flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'>
                  <Lock className='size-3' />
                  {source.locked ? 'Workflow locked' : 'Read-only'}
                </span>
              )}
              {dirty && (
                <span
                  data-testid='dirty-indicator'
                  className='rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600'
                >
                  Unsaved changes
                </span>
              )}
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              Saving creates a new commit on{' '}
              <span className='font-mono'>{source.branch}</span> and repins this
              node in a new workflow version. Old runs keep their SHA.
            </p>
          </div>
          <div className='flex items-center gap-1.5'>
            <input
              ref={fileInputRef}
              data-testid='upload-input'
              type='file'
              accept={UPLOAD_ACCEPT}
              multiple
              className='hidden'
              onChange={(event) => {
                void handleUpload(event.target.files);
                event.target.value = '';
              }}
            />
            {canEdit && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => fileInputRef.current?.click()}
                data-testid='upload-button'
              >
                <Upload className='size-3.5' />
                Upload
              </Button>
            )}
            {canEdit && dirty && (
              <Button size='sm' variant='ghost' onClick={discardChanges}>
                Discard
              </Button>
            )}
            {canEdit && (
              <Button
                size='sm'
                disabled={saveDisabled}
                onClick={() => setSaveStep('confirm')}
                data-testid='save-button'
              >
                {saveStep === 'committing' || saveStep === 'repinning' ? (
                  <Loader2 className='size-3.5 animate-spin' />
                ) : (
                  <GitCommitVertical className='size-3.5' />
                )}
                Save &amp; repin
              </Button>
            )}
          </div>
        </div>

        <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs'>
          <dt className='text-muted-foreground'>Script path</dt>
          <dd className='truncate font-mono'>
            {source.pinned ? source.script_path : scaffoldPath || '(new)'}
          </dd>
          <dt className='text-muted-foreground'>Pinned SHA</dt>
          <dd className='font-mono' title={source.pinned_sha}>
            {shortSha(source.pinned_sha)}
          </dd>
          <dt className='text-muted-foreground'>Entrypoint</dt>
          <dd className='truncate font-mono'>
            {source.entrypoint || 'main.py'}
          </dd>
        </dl>

        {!canEdit && (
          <div
            data-testid='code-editor-readonly-reason'
            className='flex gap-2 rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground'
          >
            <Lock className='mt-0.5 size-3.5 shrink-0' />
            <span>
              {source.locked
                ? 'This workflow is locked. Unlock it to edit, commit, and repin the Lambda source.'
                : 'You can inspect this source, but workflow edit permission is required to commit and repin it.'}
            </span>
          </div>
        )}

        {source.manifest_error && (
          <div className='flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-300'>
            <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
            <span>
              Manifest problem: {source.manifest_error} Fix node.json and save.
            </span>
          </div>
        )}

        {saveError && (
          <div
            data-testid='save-error'
            className='flex gap-2 rounded-md border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-700 dark:text-red-300'
          >
            <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
            <span>
              <span className='font-mono font-medium'>{saveError.code}</span> —{' '}
              {saveError.message}
            </span>
          </div>
        )}

        {lastSave && saveStep === 'done' && !dirty && (
          <div
            data-testid='save-success'
            className='flex gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs text-emerald-700 dark:text-emerald-300'
          >
            <Check className='mt-0.5 size-3.5 shrink-0' />
            <span>
              Saved: {shortSha(lastSave.oldSha)} →{' '}
              <span className='font-mono'>{shortSha(lastSave.newSha)}</span>{' '}
              (workflow version {lastSave.versionNumber}). The node pin was
              updated on the canvas.
            </span>
          </div>
        )}
      </div>

      <div className='flex min-h-0 flex-1'>
        <div className='w-44 shrink-0 overflow-y-auto border-r p-2'>
          {files.map((file) => (
            <button
              key={file.relative_path}
              type='button'
              onClick={() => setSelectedPath(file.relative_path)}
              className={cn(
                'mb-1 w-full rounded px-2 py-1.5 text-left font-mono text-xs',
                file.relative_path === selectedFile?.relative_path
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60',
              )}
              title={file.relative_path}
              data-testid={`file-tab-${file.relative_path}`}
            >
              <span className='flex items-center gap-1'>
                <span className='block truncate'>{file.relative_path}</span>
                {changedPaths.has(file.relative_path) && (
                  <span
                    data-testid={`dirty-dot-${file.relative_path}`}
                    className='size-1.5 shrink-0 rounded-full bg-amber-500'
                  />
                )}
              </span>
              {file.isNew && <span className='text-[10px]'>new</span>}
            </button>
          ))}
        </div>
        <div className='flex min-w-0 flex-1 flex-col'>
          <div className='flex h-9 shrink-0 items-center justify-between gap-2 border-b px-3'>
            <span className='truncate font-mono text-xs'>
              {selectedFile?.relative_path}
            </span>
          </div>
          <div className='min-h-0 flex-1 overflow-auto'>
            {selectedFile && (
              <CodeMirror
                key={`${selectedFile.relative_path}:${source.pinned_sha}`}
                value={selectedFile.content}
                editable={canEdit}
                readOnly={!canEdit}
                height='100%'
                style={{ height: '100%', fontSize: 12 }}
                extensions={
                  selectedFile.language === 'python'
                    ? [python()]
                    : selectedFile.language === 'json'
                      ? [json()]
                      : []
                }
                onChange={(value) =>
                  setFileContent(
                    selectedFile.relative_path,
                    value,
                    selectedFile.isNew,
                  )
                }
                data-testid='code-editor'
              />
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={saveStep === 'confirm'}
        onOpenChange={(open) => {
          if (!open && saveStep === 'confirm') setSaveStep('idle');
        }}
      >
        <DialogContent data-testid='save-confirm-dialog'>
          <DialogHeader>
            <DialogTitle>Commit and repin?</DialogTitle>
            <DialogDescription asChild>
              <div className='space-y-2 text-sm'>
                <p>
                  This validates and commits {changedPaths.size} file
                  {changedPaths.size === 1 ? '' : 's'} to{' '}
                  <span className='font-mono'>{source.branch}</span>, then
                  repins this node in a NEW workflow version.
                </p>
                <p className='font-mono text-xs'>
                  {shortSha(source.pinned_sha)} → (new commit)
                </p>
                <ul className='list-inside list-disc font-mono text-xs'>
                  {[...changedPaths].sort().map((path) => (
                    <li key={path}>{path}</li>
                  ))}
                </ul>
                <p className='text-xs text-muted-foreground'>
                  Historical runs keep their original SHA. This does not start a
                  run.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setSaveStep('idle')}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={() => void performSave()}
              data-testid='confirm-save'
            >
              Commit &amp; repin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SourceCodeWorkspaceProps {
  orgId: string;
  workflowId: string;
  runId?: string | null;
  nodeId: string;
  editRequestKey?: number;
}

/**
 * The Source tab body: the editable current pin, plus (when a run is
 * selected) a toggle to the immutable run snapshot view.
 */
export function SourceCodeWorkspace({
  orgId,
  workflowId,
  runId,
  nodeId,
  editRequestKey,
}: SourceCodeWorkspaceProps) {
  // Source is an authoring surface first. Historical source remains one click
  // away, but opening Source from a Lambda's "Edit source" action must not
  // strand the user in the immutable run snapshot.
  const [mode, setMode] = useState<'editor' | 'run'>('editor');

  useEffect(() => {
    setMode('editor');
  }, [editRequestKey, nodeId]);

  useEffect(() => {
    if (!runId && mode === 'run') setMode('editor');
  }, [runId, mode]);

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      {runId && (
        <div className='flex shrink-0 items-center gap-1 border-b px-3 py-1.5'>
          <Button
            size='sm'
            variant={mode === 'run' ? 'secondary' : 'ghost'}
            onClick={() => setMode('run')}
            data-testid='mode-run-snapshot'
          >
            Run snapshot
          </Button>
          <Button
            size='sm'
            variant={mode === 'editor' ? 'secondary' : 'ghost'}
            onClick={() => setMode('editor')}
            data-testid='mode-current-pin'
          >
            Current pin (edit)
          </Button>
        </div>
      )}
      {mode === 'run' && runId ? (
        <SourceCodePanel
          orgId={orgId}
          workflowId={workflowId}
          runId={runId}
          nodeId={nodeId}
        />
      ) : (
        <SourceCodeEditor
          orgId={orgId}
          workflowId={workflowId}
          nodeId={nodeId}
        />
      )}
    </div>
  );
}
